import React from 'react';
import { Modal, Form, Button, Typography, Checkbox } from '../../../../../../components';
import { AppstoreOutlined, FolderOpenOutlined } from '@ant-design/icons';
import { CreatableSelect } from '../../../../../../components/CreatableSelect';
import '../CreateAndManageModal.scss';
import './BaseManageModal.scss';

const { Title, Text } = Typography;

/**
 * BaseManageModal - Base component for all Manage Modal variants
 * Provides common structure and functionality that can be extended
 */
export const BaseManageModal = ({
    open,
    onCancel,
    onApply,
    title = 'Manage Source Assignment',
    description,
    selectedRowKeys = [],
    children,
    width = 400,
    showApplyButton = true,
    applyButtonText = 'Apply',
    cancelButtonText = 'Cancel',
    applyButtonDisabled = false,
    // Common form sections
    showMoveGroupField = false,
    showApplicationField = false,
    showRemoveAssignmentCheckbox = false,
    showNote = false,
    noteText = '',
    removeAssignmentText = 'Remove assignment',
    removeAssignmentDescription = '',
    moveGroups = [],
    applications = [],
    onRemoveAssignmentChange = null,
    removeAssignment = false,
    // New props for auto-filling and restrictions
    onApplicationChange = null,
    moveGroupDisabled = false,
    applicationDisabled = false,
}) => {
    const [form] = Form.useForm();

    const handleApply = async () => {
        try {
            const values = await form.validateFields();
            onApply?.(values, form);
        } catch (error) {
            console.error('Form validation failed:', error);
        }
    };

    const handleCancel = () => {
        form.resetFields();
        onCancel?.();
    };

    const selectedCount = selectedRowKeys.length;

    const renderMoveGroupField = () => (
        <>
            <Form.Item
                label={
                    <span>
                        <FolderOpenOutlined className="icon-with-label" /> Move Group
                    </span>
                }
                name="moveGroup"
            >
                <CreatableSelect
                    selectPlaceholder="Select a Move Group"
                    addItemPlaceholder="Create new Move Group"
                    options={moveGroups}
                    onChange={(value) => form.setFieldsValue({ moveGroup: value })}
                    disabled={removeAssignment || moveGroupDisabled}
                />
            </Form.Item>
            {moveGroupDisabled && (
                <Text type="secondary" className="remove-assignment-description">
                    Move group is determined by the selected application
                </Text>
            )}
        </>
    );

    const renderApplicationField = () => (
        <Form.Item
            label={
                <span>
                    <AppstoreOutlined className="icon-with-label" /> Application
                </span>
            }
            name="application"
        >
            <CreatableSelect
                selectPlaceholder="Select an Application"
                addItemPlaceholder="Create new Application"
                options={applications}
                onChange={(value) => {
                    form.setFieldsValue({ application: value });
                    onApplicationChange?.(value, form);
                }}
                disabled={removeAssignment || applicationDisabled}
            />
        </Form.Item>
    );

    const renderRemoveAssignmentCheckbox = () => (
        <div className="remove-assignment-section">
            <Checkbox checked={removeAssignment} onChange={(e) => onRemoveAssignmentChange?.(e, form)}>
                {removeAssignmentText}
            </Checkbox>
            {removeAssignmentDescription && (
                <Text type="secondary" className="remove-assignment-description">
                    {removeAssignmentDescription}
                </Text>
            )}
        </div>
    );

    const renderNote = () => (
        <Text type="secondary" className="form-note-inline">
            {noteText}
        </Text>
    );

    // TODO: Assigned Resource - Not yet implemented
    // const renderAssignedResourceField = () => (
    //   <Form.Item
    //     label="Assigned Resource"
    //     name="resource"
    //   >
    //     <Select
    //       placeholder="Select User"
    //       allowClear
    //       showSearch
    //       optionFilterProp="children"
    //       disabled={removeAssignment}
    //     >
    //       {users.length === 0 ? (
    //         <Option value={null} disabled>No Users Available</Option>
    //       ) : (
    //         users.map((user) => (
    //           <Option key={user.value} value={user.value}>
    //             {user.label}
    //           </Option>
    //         ))
    //       )}
    //     </Select>
    //   </Form.Item>
    // );

    // TODO: Target Migration Date - Not yet implemented
    // const renderTargetMigrationDateField = () => (
    //   <Form.Item
    //     label="Target Migration Date"
    //     name="date"
    //   >
    //     <DatePicker
    //       className="w-100"
    //       placeholder="Select date"
    //       disabled={removeAssignment}
    //     />
    //   </Form.Item>
    // );

    return (
        <Modal
            className="create-manage-modal base-manage-modal"
            title={
                <Title level={4} className="modal-title">
                    {title}
                </Title>
            }
            open={open}
            onCancel={handleCancel}
            footer={null}
            width={width}
            destroyOnHidden
        >
            {description && (
                <Text type="secondary" className="modal-note">
                    {description}
                </Text>
            )}

            <Form form={form} layout="vertical">
                {/* Render children - specific form fields for each variant */}
                {typeof children === 'function'
                    ? children(form, selectedCount, {
                          renderMoveGroupField,
                          renderApplicationField,
                          renderRemoveAssignmentCheckbox,
                          renderNote,
                      })
                    : children}

                {/* Common sections */}
                {showMoveGroupField && renderMoveGroupField()}
                {showApplicationField && renderApplicationField()}
                {showRemoveAssignmentCheckbox && renderRemoveAssignmentCheckbox()}
                {showNote && renderNote()}

                {/* Footer buttons */}
                <div className="button-group">
                    <Button shape="round" onClick={handleCancel}>
                        {cancelButtonText}
                    </Button>
                    {showApplyButton && (
                        <Button
                            shape="round"
                            type="primary"
                            onClick={handleApply}
                            disabled={applyButtonDisabled || selectedCount === 0}
                        >
                            {applyButtonText}
                        </Button>
                    )}
                </div>
            </Form>
        </Modal>
    );
};
